import { useNavigation, useRoute, RouteProp } from "@react-navigation/native";
import { SearchInput, StyleFunction } from "@venuepos/react-common";
import {
    Alert,
    Button,
    DataTable,
    Icon,
    Loading,
    Surface,
    useAuth,
    usePagination,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";
import {
    useCustomerGroupDeleteMutation,
    useCustomerGroupsQuery,
} from "graphql-sdk";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { useHandleMutationError } from "../../../../hooks/use-handle-mutation-error";
import { RootStackParamList } from "../../../../navigation";
import { Row } from "./row";

export function CustomerGroupsTab() {
    const auth = useAuth();
    auth.enforce("merchant.customer");

    const { navigate } = useNavigation();
    const route = useRoute<RouteProp<RootStackParamList, "CUSTOMER_GROUPS">>();
    const styles = useThemedStyle(styleFunc);
    const theme = useTheme();
    const [t] = useTranslation();

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination();
    const [search, setSearch] = useState<string>("");
    const { handleMutationError } = useHandleMutationError();

    // GraphQL
    const [deleteCustomerGroup] = useCustomerGroupDeleteMutation();
    const { data, loading, error, refetch } = useCustomerGroupsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            search: {
                query: search,
            },
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (route.params?.refetch) {
            refetch();
        }
    }, [refetch, route.params?.refetch]);

    // Actions
    const handleEdit = useCallback(
        (id: string) => {
            navigate("CUSTOMER_GROUP_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const handleDelete = useCallback(
        async (id: string) => {
            await handleMutationError(
                async () => await deleteCustomerGroup({ variables: { id } }),
                t("common.deleted", "Deleted"),
                () => {
                    navigate("ACCOUNT_PAYMENTS", { refetch: true });
                }
            );
        },
        [deleteCustomerGroup, handleMutationError, navigate, t]
    );

    const handleSearchTextChange = useCallback(
        text => {
            setSearch(text);
            // return to first page in list
            onPageChange(0);
        },
        [onPageChange]
    );

    return (
        <>
            <View style={styles.container}>
                <View style={styles.actionButtonContainer}>
                    <Button
                        style={styles.button}
                        type="secondary"
                        onPress={() => {
                            navigate("CUSTOMER_GROUP_CREATE");
                        }}
                    >
                        {t("common.create", "Create")}
                    </Button>
                </View>
            </View>
            <SearchInput onChange={handleSearchTextChange} />
            <Surface>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={sortBy === "name" && sortDirection}
                            onPress={() => onSortChange("name")}
                        >
                            {t("common.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title numeric style={styles.iconColumn}>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {error ? (
                        <Alert type="error">
                            {t(
                                "common.error",
                                "There was an error: {{errorText}}",
                                {
                                    errorText: error.message,
                                }
                            )}
                        </Alert>
                    ) : loading ? (
                        <Loading />
                    ) : (
                        data?.customerGroups.data.map((item, index) => (
                            <Row
                                item={item}
                                onSelect={() => handleEdit(item.id)}
                                onDelete={() => handleDelete(item.id)}
                                key={index.toString()}
                            />
                        ))
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.customerGroups.pagination.pages}
                        itemCount={data?.customerGroups.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        justifyContent: "space-between",
        marginBottom: theme.spacingScale * 2,
        flexWrap: "wrap",
    },
    actionButtonContainer: {
        flexDirection: "row",
        justifyContent: "flex-end",
    },
    button: {
        alignSelf: "flex-end",
        marginLeft: theme.spacingScale * 2,
    },
    iconColumn: {
        flexBasis: 100,
        flexShrink: 0,
        flexGrow: 0,
    },
});
